import _ from 'lodash';
import Combinatorics from 'js-combinatorics';

import { STATS } from 'shared/stats';
import { SLOT_MAP } from 'shared/constants';

import {
  classIdMap,
  getSpecsByMainStat,
  getSpecInfo,
} from 'shared/character';

import GameData from 'shared/gameData';

export const slotMap = SLOT_MAP;

export const qualityNameMap = {
  0: 'poor',
  1: 'common',
  2: 'uncommon',
  3: 'rare',
  4: 'epic',
  5: 'legendary',
  6: 'artifact',
  7: 'heirloom',
};

export const FISHING_POLE = {
  itemClass: 2,
  itemSubClass: 20,
};

export const INVALID_WEAPON_TYPES = [{
  itemClass: 2,
  itemSubClass: 14,
}];

const invTypeToHands = {
  13: '1h', // Weapon
  14: 'oh', // Shield
  15: '2h', // Ranged (Bow, XBow, Gun)
  17: '2h', // Two-Hand
  21: 'mh', // Main Hand
  22: 'oh', // Off Hand
  23: 'oh', // Holdable (Tome)
  26: 'mh', // MH Ranged (Wand / Gun)

  // 25 - Thrown (removed)
  // 27 - Quiver
  // 28 - Relic
};


// overrides for a few cases
const weaponClassDetailsRaw = [{
  // 1h axe
  itemClass: 2,
  itemSubClass: 0,
  specsCanDrop: [
    // str
    ['paladin', 'protection'],
    ['warrior', 'protection'],
    ['warrior', 'fury'],
    ['deathknight', 'frost'],

    // int
    ['paladin', 'holy'],
    ['shaman', 'elemental'],
    ['shaman', 'restoration'],
    ['monk', 'mistweaver'],

    // agi
    ['demon_hunter', 'havoc'],
    ['demon_hunter', 'vengeance'],
    ['rogue', 'outlaw'],
    ['shaman', 'enhancement'],
    ['monk', 'windwalker'],
    ['monk', 'brewmaster'],
  ],
  specsAlsoCanUse: [
    ['rogue', 'assassination'],
  ],
}, {
  // 2h axe
  itemClass: 2,
  itemSubClass: 1,
  specsCanDrop: [
    ['paladin', 'retribution'],
    ['warrior', 'fury'],
    ['warrior', 'arms'],
    ['deathknight', 'blood'],
    ['deathknight', 'unholy'],
    ['deathknight', 'frost'],
  ],
}, {
  // gun
  itemClass: 2,
  itemSubClass: 2,
  hand: '2h',
  specsCanDrop: [
    ['hunter', 'beast_mastery'],
    ['hunter', 'marksmanship'],
  ],
}, {
  // gun
  itemClass: 2,
  itemSubClass: 3,
  hand: '2h',
  specsCanDrop: [
    ['hunter', 'beast_mastery'],
    ['hunter', 'marksmanship'],
  ],
}, {
  // 1h mace
  itemClass: 2,
  itemSubClass: 4,
  specsCanDrop: [
    // tanks
    ['paladin', 'protection'],
    ['warrior', 'fury'],
    ['warrior', 'protection'],
    ['deathknight', 'frost'],

    // casters/healers
    ['paladin', 'holy'],
    ['druid', 'balance'],
    ['druid', 'restoration'],
    ['priest', 'discipline'],
    ['priest', 'holy'],
    ['priest', 'shadow'],
    ['shaman', 'elemental'],
    ['shaman', 'restoration'],
    ['monk', 'mistweaver'],

    // agi
    ['rogue', 'outlaw'],
    ['shaman', 'enhancement'],
    ['monk', 'windwalker'],
    ['monk', 'brewmaster'],
  ],
}, {
  // 2h mace
  itemClass: 2,
  itemSubClass: 5,
  specsCanDrop: [
    ['paladin', 'retribution'],
    ['warrior', 'fury'],
    ['warrior', 'arms'],
    ['deathknight', 'blood'],
    ['deathknight', 'unholy'],
    ['deathknight', 'frost'],
  ],
  specsAlsoCanUse: [
    ['druid', 'balance'],
    ['druid', 'restoration'],
  ],
}, {
  // polearm
  itemClass: 2,
  itemSubClass: 6,
  specsCanDrop: [
    // agi
    ['druid', 'feral'],
    ['druid', 'guardian'],
    ['hunter', 'survival'],
    ['monk', 'windwalker'],
    ['monk', 'brewmaster'],

    // str
    ['paladin', 'retribution'],
    ['warrior', 'arms'],
    ['warrior', 'fury'],
    ['deathknight', 'blood'],
    ['deathknight', 'unholy'],
    ['deathknight', 'frost'],
  ],
  specsAlsoCanUse: [
    ['druid', 'balance'],
  ],
}, {
  // 1h sword
  itemClass: 2,
  itemSubClass: 7,
  specsCanDrop: [
    // tank
    ['paladin', 'protection'],
    ['warrior', 'fury'],
    ['warrior', 'protection'],
    ['deathknight', 'frost'],

    // agi sword
    ['rogue', 'outlaw'],
    ['monk', 'windwalker'],
    ['monk', 'brewmaster'],
    ['demon_hunter', 'havoc'],
    ['demon_hunter', 'vengeance'],

    // int sword
    ['mage', 'arcane'],
    ['mage', 'fire'],
    ['mage', 'frost'],
    ['paladin', 'holy'],
    ['warlock', 'affliction'],
    ['warlock', 'demonology'],
    ['warlock', 'destruction'],
    ['monk', 'mistweaver'],
  ],
  specsAlsoCanUse: [
    ['rogue', 'assassination'],
  ],
}, {
  // 2h sword
  itemClass: 2,
  itemSubClass: 8,
  specsCanDrop: [
    ['paladin', 'retribution'],
    ['warrior', 'fury'],
    ['warrior', 'arms'],
    ['deathknight', 'blood'],
    ['deathknight', 'unholy'],
    ['deathknight', 'frost'],
  ],
}, {
  // warglaive
  itemClass: 2,
  itemSubClass: 9,
  specsCanDrop: [
    ['demon_hunter', 'havoc'],
    ['demon_hunter', 'vengeance'],
  ],
}, {
  // staff
  itemClass: 2,
  itemSubClass: 10,
  specsCanDrop: [
    // int
    ['mage', 'arcane'],
    ['mage', 'fire'],
    ['mage', 'frost'],
    ['druid', 'balance'],
    ['druid', 'restoration'],
    ['priest', 'discipline'],
    ['priest', 'holy'],
    ['priest', 'shadow'],
    ['shaman', 'elemental'],
    ['shaman', 'restoration'],
    ['warlock', 'affliction'],
    ['warlock', 'demonology'],
    ['warlock', 'destruction'],
    ['monk', 'mistweaver'],

    // agi
    ['druid', 'feral'],
    ['druid', 'guardian'],
    ['hunter', 'survival'],
    ['monk', 'windwalker'],
    ['monk', 'brewmaster'],
  ],
  specsAlsoCanUse: [
    ['warrior', 'arms'],
    ['warrior', 'fury'],
  ],
}, {
  // fist
  itemClass: 2,
  itemSubClass: 13,
  specsCanDrop: [
    // agi
    ['rogue', 'outlaw'],
    ['shaman', 'enhancement'],
    ['monk', 'windwalker'],
    ['monk', 'brewmaster'],
    ['demon_hunter', 'havoc'],
    ['demon_hunter', 'vengeance'],
  ],
  specsAlsoCanUse: [
    ['shaman', 'elemental'],
  ],
}, {
  // dagger
  itemClass: 2,
  itemSubClass: 15,
  specsCanDrop: [
    // int
    ['mage', 'arcane'],
    ['mage', 'fire'],
    ['mage', 'frost'],
    ['druid', 'balance'],
    ['druid', 'restoration'],
    ['priest', 'discipline'],
    ['priest', 'holy'],
    ['priest', 'shadow'],
    ['shaman', 'elemental'],
    ['shaman', 'restoration'],
    ['warlock', 'affliction'],
    ['warlock', 'demonology'],
    ['warlock', 'destruction'],

    // agi
    ['rogue', 'assassination'],
    ['rogue', 'subtlety'],
  ],
  specsAlsoCanUse: [
    ['rogue', 'outlaw'],
  ],
}, {
  // bow/crossbow
  itemClass: 2,
  itemSubClass: 18,
  hand: '2h',
  specsCanDrop: [
    ['hunter', 'beast_mastery'],
    ['hunter', 'marksmanship'],
  ],
}, {
  // wand
  itemClass: 2,
  itemSubClass: 19,
  hand: 'mh',
  specsCanDrop: [
    ['mage', 'arcane'],
    ['mage', 'fire'],
    ['mage', 'frost'],
    ['priest', 'discipline'],
    ['priest', 'holy'],
    ['priest', 'shadow'],
    ['warlock', 'affliction'],
    ['warlock', 'demonology'],
    ['warlock', 'destruction'],
  ],
}, {
  // misc (off hands, tomes)
  itemClass: 4,
  itemSubClass: 0,
  hand: 'oh',
  noEnchant: true,
  specsCanDrop: [
    ['mage', 'arcane'],
    ['mage', 'fire'],
    ['mage', 'frost'],
    ['paladin', 'holy'],
    ['druid', 'balance'],
    ['druid', 'restoration'],
    ['priest', 'discipline'],
    ['priest', 'holy'],
    ['priest', 'shadow'],
    ['warlock', 'affliction'],
    ['warlock', 'demonology'],
    ['warlock', 'destruction'],
    ['monk', 'mistweaver'],
  ],
  specsAlsoCanUse: [
    ['shaman', 'elemental'],
  ],
}, {
  // shield
  itemClass: 4,
  itemSubClass: 6,
  hand: 'oh',
  noEnchant: true,
  specsCanDrop: [
    ['paladin', 'holy'],
    ['paladin', 'protection'],
    ['warrior', 'protection'],
    ['shaman', 'elemental'],
    ['shaman', 'restoration'],
  ],
}];

// used for wrathion socket viability
const ETERNAL_PALACE_ITEM_IDS = new Set([
  168273,
  168274,
  168275,
  168276,
  168301,
  168306,
  168335,
  168336,
  168337,
  168338,
  168339,
  168341,
  168342,
  168343,
  168345,
  168346,
  168347,
  168348,
  168349,
  168350,
  168352,
  168353,
  168354,
  168355,
  168357,
  168359,
  168360,
  168361,
  168362,
  168363,
  168364,
  168365,
  168367,
  168371,
  168372,
  168373,
  168374,
  168375,
  168376,
  168377,
  168378,
  168379,
  168380,
  168381,
  168382,
  168383,
  168384,
  168385,
  168386,
  168387,
  168388,
  168389,
  168390,
  168391,
  168397,
  168475,
  168476,
  168477,
  168478,
  168604,
  168818,
  168868,
  168869,
  168870,
  168871,
  168872,
  168873,
  168874,
  168875,
  168876,
  168877,
  168878,
  168879,
  168880,
  168881,
  168882,
  168883,
  168884,
  168885,
  168886,
  168887,
  168888,
  168889,
  168890,
  168891,
  168892,
  168893,
  168894,
  168896,
  168897,
  168898,
  168899,
  168900,
  168901,
  168902,
  168903,
  168904,
  168905,
  169304,
  169305,
  169306,
  169307,
  169308,
  169309,
  169310,
  169311,
  169312,
  169313,
  169314,
  169315,
  169316,
  169319,
  169588,
]);


const weaponClassDetails = _.map(weaponClassDetailsRaw, details => {
  const specsCanDrop = _.map(details.specsCanDrop, spec => _.get(getSpecInfo(spec[0], spec[1]), 'id'));
  const specsCanUse = _.union(
    specsCanDrop,
    _.map(details.specsAlsoCanUse, spec => _.get(getSpecInfo(spec[0], spec[1]), 'id')),
  );
  return {
    ...details,
    specsCanDrop,
    specsCanUse,
  };
});

export const getWeaponHands = (weapon) => {
  // make sure we have an item
  if (!weapon || !weapon.itemClass) {
    return null;
  }
  // reject items that are not in the main inv type weapon list (filters out rings)
  if (!invTypeToHands[weapon.inventoryType]) {
    return null;
  }
  const override = _.find(weaponClassDetails, { itemClass: weapon.itemClass, itemSubClass: weapon.itemSubClass });
  return _.get(override, 'hand', invTypeToHands[weapon.inventoryType]);
};

export const isWeaponEnchantable = weapon => {
  const info = _.find(weaponClassDetails, { itemClass: weapon.itemClass, itemSubClass: weapon.itemSubClass });
  if (info && info.noEnchant) {
    return false;
  }
  if (weapon.quality === 6) {
    return false;
  }
  return true;
};

export const LEGION_LEGENDARY_CATEGORY = 357;

export const invertedQualityNameMap = _.mapValues(_.invert(qualityNameMap), parseInt);

export const invertedSlotMap = _.mapValues(_.invert(slotMap), slotId => parseInt(slotId, 10));

export const armorSubClasses = [{
  itemSubClass: 0,
  key: 'misc',
  name: 'Misc',
}, {
  itemSubClass: 1,
  key: 'cloth',
  name: 'Cloth',
}, {
  itemSubClass: 2,
  key: 'leather',
  name: 'Leather',
}, {
  itemSubClass: 3,
  key: 'mail',
  name: 'Mail',
}, {
  itemSubClass: 4,
  key: 'plate',
  name: 'Plate',
}, {
  itemSubClass: 6,
  key: 'shield',
  name: 'Shield',
}];

const MAIN_HAND_SLOT = invertedSlotMap.main_hand;

const mainStatMap = {
  agi: ['STAT_AGILITY', 'STAT_AGI_INT', 'STAT_STR_AGI', 'STAT_STR_AGI_INT'],
  int: ['STAT_INTELLECT', 'STAT_AGI_INT', 'STAT_STR_INT', 'STAT_STR_AGI_INT'],
  str: ['STAT_STRENGTH', 'STAT_STR_AGI', 'STAT_STR_INT', 'STAT_STR_AGI_INT'],
};

const mainStatIdMap = _.mapValues(
  mainStatMap,
  statEnums => _.map(statEnums, enumName => _.get(_.find(STATS, { enum: enumName }), 'dataId')),
);

const allMainStats = _.flatten(_.values(mainStatIdMap));

const itemHasAnyMainStat = item => {
  let hasStat = false;
  _.each(item.stats, stat => {
    hasStat = hasStat || _.includes(allMainStats, stat.id);
  });
  return hasStat;
};

const itemHasMainStat = (item, mainStatShortName) => {
  let hasStat = false;
  _.each(item.stats, stat => {
    hasStat = hasStat || _.includes(mainStatIdMap[mainStatShortName], stat.id);
  });
  return hasStat;
};

export const isCloak = item => {
  return item.itemClass === 4 && item.inventoryType === parseInt(invertedSlotMap.back, 10);
};

// Test if an item is usable or if it can drop
// This is private since it has the ugly boolean parameter
// Use the exported isItemUsable or doesItemDrop below
export const itemNotUsableReason = (item, classSearch, specSearch, onlyDrop = false) => {
  const specInfo = getSpecInfo(classSearch, specSearch);

  if (!specInfo) {
    return 'no_spec_info';
  }

  // jewelry is usable by anyone
  if (_.includes(['finger', 'neck'], slotMap[item.inventoryType])) {
    return null;
  }

  if (item.allowableClasses) {
    if (!_.includes(item.allowableClasses, specInfo.class.id)) {
      return 'class_cannot_use';
    }
  }

  // check spec override first
  if (item.specs) {
    if (_.includes(item.specs, specInfo.id)) {
      return null;
    }
    return 'spec_cannot_use';
  }

  // check armor class
  if (item.itemClass === 4) {
    // skip cloaks, anyone can use
    if (item.inventoryType !== parseInt(invertedSlotMap.back, 10)) {
      const armorSubClass = _.find(armorSubClasses, { itemSubClass: item.itemSubClass });
      if (armorSubClass) {
        if (!_.includes(specInfo.class.armorTypes, armorSubClass.key)) {
          return 'wrong_armor_type';
        }
      }
    }
  }

  // check weapon/spec table
  if (_.includes(['main_hand', 'off_hand'], slotMap[item.inventoryType])) {
    const weaponInfo = _.find(weaponClassDetails, { itemClass: item.itemClass, itemSubClass: item.itemSubClass });
    if (weaponInfo) {
      if (onlyDrop) {
        if (!_.includes(weaponInfo.specsCanDrop, specInfo.id)) {
          return 'item_does_not_drop';
        }
      } else if (!_.includes(weaponInfo.specsCanUse, specInfo.id)) {
        return 'spec_cannot_use';
      }
    }
  }

  // if there is no main stat at all, probably anyone can use the item.
  // This was initially an issue with Azshara's Font of Power but several SL items appear to use the asme
  // item categories that mess up the old analysis
  if (item.stats && !itemHasAnyMainStat(item)) {
    return null;
  }

  // check for main stat on armor pieces
  if (item.stats && !itemHasMainStat(item, specInfo.mainStat)) {
    return 'wrong_main_stat';
  }

  return null;
};

export const isItemUsable = (item, classSearch, specSearch) => {
  return !itemNotUsableReason(item, classSearch, specSearch, false);
};

export const doesItemDrop = (item, classSearch, specSearch) => {
  return !itemNotUsableReason(item, classSearch, specSearch, true);
};

export const enchants = [{
// WEAPON

  // death knight weapon enchants

  // shadowlands DK runeforges
  slot: MAIN_HAND_SLOT,
  name: 'Rune of the Apocalypse',
  shortName: 'Apoc',
  key: 'rune_of_the_apocalypse',
  id: 6245,
  icon: 'spell_deathknight_thrash_ghoul',
  spellId: 327082,
  requirement: {
    classes: [classIdMap['Death Knight']],
  },
  preferred: {
    roles: ['melee', 'tank'],
  },
  group: 'death_knight',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Rune of Hysteria',
  shortName: 'Hyst',
  key: 'rune_of_hysteria',
  id: 6243,
  icon: 'ability_deathknight_runicimpowerment',
  spellId: 326911,
  requirement: {
    classes: [classIdMap['Death Knight']],
  },
  preferred: {
    roles: ['melee', 'tank'],
  },
  group: 'death_knight',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Rune of Sanguination',
  shortName: 'Sang',
  key: 'rune_of_sanguination',
  id: 6241,
  icon: 'ability_argus_deathfog',
  spellId: 326805,
  requirement: {
    classes: [classIdMap['Death Knight']],
  },
  preferred: {
    roles: ['melee', 'tank'],
  },
  group: 'death_knight',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Rune of Spellwarding',
  shortName: 'Ward',
  key: 'rune_of_spellwarding',
  id: 6242,
  icon: 'spell_fire_twilightfireward',
  spellId: 326855,
  requirement: {
    classes: [classIdMap['Death Knight']],
  },
  preferred: {
    roles: ['tank'],
  },
  group: 'death_knight',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Rune of Unending Thirst',
  shortName: 'Thirst',
  key: 'rune_of_unending_thirst',
  id: 6244,
  icon: 'spell_nzinsanity_bloodthirst',
  spellId: 326977,
  requirement: {
    classes: [classIdMap['Death Knight']],
  },
  preferred: {
    roles: ['melee', 'tank'],
  },
  group: 'death_knight',
}, {


  slot: MAIN_HAND_SLOT,
  name: 'Rune of Razorice',
  shortName: 'Razor',
  key: 'rune_of_razorice',
  id: 3370,
  icon: 'spell_frost_frostarmor',
  spellId: 53343,
  requirement: {
    classes: [classIdMap['Death Knight']],
  },
  preferred: {
    roles: ['melee'],
  },
  group: 'death_knight',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Rune of the Fallen Crusader',
  shortName: 'Fallen',
  key: 'rune_of_the_fallen_crusader',
  id: 3368,
  icon: 'spell_holy_retributionaura',
  spellId: 53344,
  requirement: {
    classes: [classIdMap['Death Knight']],
  },
  preferred: {
    roles: ['melee', 'tank'],
  },
  group: 'death_knight',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Rune of the Stoneskin Gargoyle',
  shortName: 'Stone',
  key: 'rune_of_the_stoneskin_gargoyle',
  id: 3847,
  icon: 'inv_sword_130',
  spellId: 62158,
  requirement: {
    classes: [classIdMap['Death Knight']],
  },
  preferred: {
    roles: ['tank'],
  },
  group: 'death_knight',
}, {

  // Shadowlands Weapon Enchants

  slot: MAIN_HAND_SLOT,
  name: 'Ascended Vigor',
  shortName: 'Vigor',
  key: 'ascended_vigor',
  id: 6227,
  itemId: 172365,
  preferred: {
    roles: ['tank'],
  },
  group: 'shadowlands',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Celestial Guidance',
  shortName: 'Celest',
  key: 'celestial_guidance',
  id: 6229,
  itemId: 172366,
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  group: 'shadowlands',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Eternal Grace',
  shortName: 'Grace',
  key: 'eternal_grace',
  id: 6226,
  itemId: 172367,
  preferred: {
    roles: ['healer'],
  },
  group: 'shadowlands',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Lightless Force',
  shortName: 'Force',
  key: 'lightless_force',
  id: 6223,
  itemId: 172370,
  preferred: {
    roles: ['caster', 'tank', 'ranged', 'melee'],
  },
  group: 'shadowlands',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Sinful Revelation',
  shortName: 'Sin',
  key: 'sinful_revelation',
  id: 6228,
  itemId: 172368,
  preferred: {
    roles: ['caster', 'tank', 'ranged', 'melee'],
  },
  group: 'shadowlands',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Optical Target Embiggener',
  shortName: 'Optic',
  key: 'optical_target_embiggener',
  id: 6196,
  icon: 'inv_engineering_90_scope_blue',
  itemId: 172920,
  requirement: {
    classes: [
      classIdMap.Hunter,
    ],
  },
  preferred: {
    roles: ['ranged'],
  },
  group: 'engineering',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Infra-green Reflex Sight',
  shortName: 'Infra',
  key: 'infragreen_reflex_sight',
  id: 6195,
  icon: 'inv_engineering_90_scope_green',
  itemId: 172921,
  requirement: {
    classes: [
      classIdMap.Hunter,
    ],
  },
  preferred: {
    roles: ['ranged'],
  },
  group: 'engineering',
}, {

  // BFA Weapon Enchants

  slot: MAIN_HAND_SLOT,
  name: 'Deadly Navigation',
  shortName: 'Dead',
  key: 'deadly_navigation',
  id: 5965,
  itemId: 159785,
  group: 'bfa',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Quick Navigation',
  shortName: 'Quick',
  key: 'quick_navigation',
  id: 5963,
  itemId: 159786,
  group: 'bfa',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Masterful Navigation',
  shortName: 'Mast',
  key: 'masterful_navigation',
  id: 5964,
  itemId: 159787,
  group: 'bfa',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Versatile Navigation',
  shortName: 'Vers',
  key: 'versatile_navigation',
  id: 5962,
  itemId: 159788,
  group: 'bfa',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Stalwart Navigation',
  shortName: 'Stam',
  key: 'stalwart_navigation',
  id: 5966,
  itemId: 159789,
  group: 'bfa',

}, {
  slot: MAIN_HAND_SLOT,
  name: 'Force Multiplier',
  shortName: 'Force',
  key: 'force_multiplier',
  id: 6148,
  itemId: 168596,
  group: 'bfa',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Machinist\'s Brilliance',
  shortName: 'Bril',
  key: 'machinists_brilliance',
  id: 6112,
  itemId: 168593,
  group: 'bfa',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Oceanic Restoration',
  shortName: 'Resto',
  key: 'oceanic_restoration',
  id: 6149,
  itemId: 168592,
  group: 'bfa',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Naga Hide',
  shortName: 'Naga',
  key: 'naga_hide',
  id: 6150,
  itemId: 168598,
  group: 'bfa',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Coastal Surge',
  shortName: 'Coast',
  key: 'coastal_surge',
  id: 5946,
  itemId: 153476,
  group: 'bfa',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Gale-Force Striking',
  shortName: 'Gale',
  key: 'galeforce_striking',
  id: 5950,
  itemId: 153480,
  group: 'bfa',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Torrent of Elements',
  shortName: 'Torr',
  key: 'torrent_of_elements',
  id: 5949,
  itemId: 153479,
  group: 'bfa',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Siphoning',
  shortName: 'Siph',
  key: 'siphoning',
  id: 5948,
  itemId: 153478,
  group: 'bfa',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Crow\'s Nest Scope',
  shortName: 'Crow',
  key: 'crows_nest_scope',
  id: 5955,
  icon: 'inv_eng_crowsnestscope',
  itemId: 158212,
  requirement: {
    classes: [
      classIdMap.Hunter,
    ],
  },
  group: 'engineering',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Frost-Laced Ammunition',
  shortName: 'Frost',
  key: 'frostlaced_ammunition',
  id: 5958,
  icon: 'inv_eng_frostlacedammunition',
  itemId: 158377,
  requirement: {
    classes: [
      classIdMap.Hunter,
    ],
  },
  group: 'engineering',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Incendiary Ammunition',
  shortName: 'Incen',
  key: 'incendiary_ammunition',
  id: 5957,
  icon: 'inv_eng_incendiaryammunition',
  itemId: 158203,
  requirement: {
    classes: [
      classIdMap.Hunter,
    ],
  },
  group: 'engineering',
}, {
  slot: MAIN_HAND_SLOT,
  name: 'Monelite Scope of Alacrity',
  shortName: 'Monel',
  key: 'monelite_scope_of_alacrity',
  id: 5956,
  icon: 'inv_eng_monalitescopeofalacrity',
  itemId: 158327,
  requirement: {
    classes: [
      classIdMap.Hunter,
    ],
  },
  group: 'engineering',
}, {

  // RING

  // Shadowlands Ring Enchants

  slot: 11,
  name: '16 Critical Strike',
  shortName: 'Crit',
  key: '16crit',
  id: 6164,
  itemId: 172361,
  group: 'tenet',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  stat: {
    type: 'crit',
    amount: 16,
  },
}, {
  slot: 11,
  name: '16 Haste',
  shortName: 'Haste',
  key: '16haste',
  id: 6166,
  itemId: 172362,
  group: 'tenet',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  stat: {
    type: 'haste',
    amount: 16,
  },
}, {
  slot: 11,
  name: '16 Mastery',
  shortName: 'Mast',
  key: '16mastery',
  id: 6168,
  itemId: 172363,
  group: 'tenet',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  stat: {
    type: 'mastery',
    amount: 16,
  },
}, {
  slot: 11,
  name: '16 Versatility',
  shortName: 'Vers',
  key: '16vers',
  id: 6170,
  itemId: 172364,
  group: 'tenet',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  stat: {
    type: 'vers',
    amount: 16,
  },
}, {
  slot: 11,
  name: '12 Critical Strike',
  shortName: 'Crit',
  key: '12crit',
  id: 6163,
  itemId: 172357,
  group: 'bargain',
  stat: {
    type: 'crit',
    amount: 12,
  },
}, {
  slot: 11,
  name: '12 Haste',
  shortName: 'Haste',
  key: '12haste',
  id: 6165,
  itemId: 172358,
  group: 'bargain',
  stat: {
    type: 'haste',
    amount: 12,
  },
}, {
  slot: 11,
  name: '12 Mastery',
  shortName: 'Mastery',
  key: '12mastery',
  id: 6167,
  itemId: 172359,
  group: 'bargain',
  stat: {
    type: 'mastery',
    amount: 12,
  },
}, {
  slot: 11,
  name: '12 Versatility',
  shortName: 'Vers',
  key: '12vers',
  id: 6169,
  itemId: 172360,
  group: 'bargain',
  stat: {
    type: 'vers',
    amount: 12,
  },
}, {

  // BfA Ring Enchants

  slot: 11,
  name: '9 Critical Strike',
  shortName: 'Crit',
  key: '9crit',
  id: 6108,
  itemId: 168446,
  group: 'accord',
}, {
  slot: 11,
  name: '9 Haste',
  shortName: 'Haste',
  key: '9haste',
  id: 6109,
  itemId: 168447,
  group: 'accord',
}, {
  slot: 11,
  name: '9 Mastery',
  shortName: 'Mast',
  key: '9mastery',
  id: 6110,
  itemId: 168448,
  group: 'accord',
}, {
  slot: 11,
  name: '9 Versatility',
  shortName: 'Vers',
  key: '9vers',
  id: 6111,
  itemId: 168449,
  group: 'accord',
}, {

  slot: 11,
  name: '6 Critical Strike',
  shortName: 'Crit',
  key: '6crit',
  id: 5942,
  itemId: 153442,
  group: 'pact',
}, {
  slot: 11,
  name: '6 Haste',
  shortName: 'Haste',
  key: '6haste',
  id: 5943,
  itemId: 153443,
  group: 'pact',
}, {
  slot: 11,
  name: '6 Mastery',
  shortName: 'Mast',
  key: '6mastery',
  id: 5944,
  itemId: 153444,
  group: 'pact',
}, {
  slot: 11,
  name: '6 Versatility',
  shortName: 'Vers',
  key: '6vers',
  id: 5945,
  itemId: 153445,
  group: 'pact',
}, {

  slot: 11,
  name: '4 Critical Strike',
  shortName: 'Crit',
  key: '4crit',
  id: 5938,
  itemId: 153438,
  group: 'seal',
}, {
  slot: 11,
  name: '4 Haste',
  shortName: 'Haste',
  key: '4haste',
  id: 5939,
  itemId: 153439,
  group: 'seal',
}, {
  slot: 11,
  name: '4 Mastery',
  shortName: 'Mast',
  key: '4mastery',
  id: 5940,
  itemId: 153440,
  group: 'seal',
}, {
  slot: 11,
  name: '4 Versatility',
  shortName: 'Vers',
  key: '4vers',
  id: 5941,
  itemId: 153441,
  group: 'seal',
}, {

  // CLOAK ENCHANTS

  slot: invertedSlotMap.back,
  name: 'Fortified Avoidance',
  shortName: 'Avoid',
  key: 'fortified_avoidance',
  id: 6203,
  itemId: 172411,
  group: 'stam',
}, {
  slot: invertedSlotMap.back,
  name: 'Fortified Leech',
  shortName: 'Leech',
  key: 'fortified_leech',
  id: 6204,
  itemId: 172412,
  group: 'stam',
}, {
  slot: invertedSlotMap.back,
  name: 'Fortified Speed',
  key: 'fortified_speed',
  shortName: 'Speed',
  id: 6202,
  itemId: 172410,
  group: 'stam',
}, {
  slot: invertedSlotMap.back,
  name: 'Soul Vitality',
  shortName: 'Stam',
  key: 'soul_vitality',
  id: 6208,
  itemId: 177660,
  group: 'stam',
}, {

  // CHEST ENCHANTS

  slot: invertedSlotMap.chest,
  name: 'Eternal Bounds',
  shortName: 'Int',
  key: 'eternal_bounds',
  id: 6217,
  itemId: 177715,
  group: 'stats',
  preferred: {
    specs: _.map(getSpecsByMainStat('int'), 'id'),
  },
}, {
  slot: invertedSlotMap.chest,
  name: 'Eternal Bulwark',
  shortName: 'Bulw',
  key: 'eternal_bulwark',
  id: 6213,
  itemId: 172418,
  group: 'stats',
  preferred: {
    roles: ['tank'],
  },
}, {
  slot: invertedSlotMap.chest,
  name: 'Eternal Skirmish',
  shortName: 'Skir',
  key: 'eternal_skirmish',
  id: 6214,
  itemId: 177659,
  group: 'stats',
  preferred: {
    roles: ['tank', 'melee', 'ranged'],
  },
}, {
  slot: invertedSlotMap.chest,
  name: 'Eternal Stats',
  shortName: 'Stats',
  key: 'eternal_stats',
  id: 6230,
  itemId: 177962,
  group: 'stats',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
}, {
  slot: invertedSlotMap.chest,
  name: 'Sacred Stats',
  shortName: 'Stats',
  key: 'sacred_stats',
  id: 6216,
  itemId: 177716,
  group: 'stats',
}, {
  slot: invertedSlotMap.chest,
  name: 'Eternal Insight',
  shortName: 'Ins',
  key: 'eternal_insight',
  id: 6265,
  itemId: 183738,
  group: 'stats',
  preferred: {
    roles: ['caster', 'healer'],
  },

  // BRACER ENCHANTS

}, {
  slot: invertedSlotMap.wrist,
  name: 'Eternal Intellect',
  shortName: 'Int',
  key: 'eternal_intellect',
  id: 6220,
  itemId: 172415,
  group: 'stats',
  preferred: {
    specs: _.map(getSpecsByMainStat('int'), 'id'),
  },
}, {
  slot: invertedSlotMap.wrist,
  name: 'Illuminated Soul',
  shortName: 'Int',
  key: 'illuminated_soul',
  id: 6219,
  itemId: 172414,
  group: 'stats',
}, {
  slot: invertedSlotMap.wrist,
  name: 'Shaded Hearthing',
  shortName: 'Hrth',
  key: 'shaded_hearthing',
  id: 6222,
  itemId: 172416,
  group: 'stats',
  zeroDps: true,

  // GLOVE ENCHANTS

}, {
  slot: invertedSlotMap.hands,
  name: 'Eternal Strength',
  shortName: 'Str',
  key: 'eternal_strength',
  id: 6210,
  itemId: 172408,
  group: 'stats',
  preferred: {
    specs: _.map(getSpecsByMainStat('str'), 'id'),
  },
}, {
  slot: invertedSlotMap.hands,
  name: 'Strength of Soul',
  shortName: 'Str',
  key: 'strength_of_soul',
  id: 6209,
  itemId: 172407,
  group: 'stats',
}, {
  slot: invertedSlotMap.hands,
  name: 'Shadowlands Gathering',
  shortName: 'Gthr',
  key: 'shadowlands_gathering',
  id: 6205,
  itemId: 172406,
  group: 'stats',
  zeroDps: true,

  // BOOT ENCHANTS

}, {
  slot: invertedSlotMap.feet,
  name: 'Eternal Agility',
  shortName: 'Agi',
  key: 'eternal_agility',
  id: 6211,
  itemId: 172419,
  group: 'stats',
  preferred: {
    specs: _.map(getSpecsByMainStat('agi'), 'id'),
  },
}, {
  slot: invertedSlotMap.feet,
  name: 'Agile Soulwalker',
  shortName: 'Agi',
  key: 'agile_soulwalker',
  id: 6212,
  itemId: 172413,
  group: 'stats',
}, {
  slot: invertedSlotMap.feet,
  name: 'Soul Treads',
  shortName: 'Speed',
  key: 'soul_treads',
  id: 6207,
  itemId: 177661,
  group: 'stats',
  zeroDps: true,

}];

export const socketBonuses = _.union(
  [1808], // default socket bonus ID to use when making faking sockets
  _.map(_.keys(GameData.BonusSockets), b => parseInt(b, 10)),
);
/*
[
  1808,
  4802,
  523,
];
*/

export const secondaryBonuses = [{
  bonusId: 603,
  stat: 'crit',
  name: 'Crit',
}, {
  bonusId: 604,
  stat: 'haste',
  name: 'Haste',
}, {
  bonusId: 605,
  stat: 'mastery',
  name: 'Mastery',
}, {
  bonusId: 607,
  stat: 'vers',
  name: 'Versatility',
}];

export const gemInfo = [{

  //////////////////////////////////////////////////
  // Domination Gems
  //////////////////////////////////////////////////

  shortName: 'shard_of_bek',
  name: 'Shard of Bek',
  id: 187057,
  quality: 4,
  icon: 'inv_enchanting_wod_crystalshard',
  type: 'DOMINATION',
  group: 'domination-blood',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationBek',
  dps: true,
  rank: 1,
}, {
  shortName: 'ominous_shard_of_bek',
  name: 'Ominous Bek',
  id: 187284,
  quality: 4,
  icon: 'inv_enchanting_wod_crystalshard',
  type: 'DOMINATION',
  group: 'domination-blood',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationBek',
  dps: true,
  rank: 2,
}, {
  shortName: 'desolate_shard_of_bek',
  name: 'Desolate Bek',
  id: 187293,
  quality: 4,
  icon: 'inv_enchanting_wod_crystalshard',
  type: 'DOMINATION',
  group: 'domination-blood',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationBek',
  dps: true,
  rank: 3,
}, {
  shortName: 'foreboding_shard_of_bek',
  name: 'Foreboding Bek',
  id: 187302,
  quality: 4,
  icon: 'inv_enchanting_wod_crystalshard',
  type: 'DOMINATION',
  group: 'domination-blood',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationBek',
  dps: true,
  rank: 4,
}, {
  shortName: 'portentous_shard_of_bek',
  name: 'Portentous Bek',
  id: 187312,
  quality: 4,
  icon: 'inv_enchanting_wod_crystalshard',
  type: 'DOMINATION',
  group: 'domination-blood',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationBek',
  dps: true,
  rank: 5,
}, {
  shortName: 'shard_of_jas',
  name: 'Shard of Jas',
  id: 187059,
  quality: 4,
  icon: 'inv_enchanting_wod_crystalshard',
  type: 'DOMINATION',
  group: 'domination-blood',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationJas',
  rank: 1,
}, {
  shortName: 'ominous_shard_of_jas',
  name: 'Ominous Jas',
  id: 187285,
  quality: 4,
  icon: 'inv_enchanting_wod_crystalshard',
  type: 'DOMINATION',
  group: 'domination-blood',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationJas',
  rank: 2,
}, {
  shortName: 'desolate_shard_of_jas',
  name: 'Desolate Jas',
  id: 187294,
  quality: 4,
  icon: 'inv_enchanting_wod_crystalshard',
  type: 'DOMINATION',
  group: 'domination-blood',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationJas',
  rank: 3,
}, {
  shortName: 'foreboding_shard_of_jas',
  name: 'Foreboding Jas',
  id: 187303,
  quality: 4,
  icon: 'inv_enchanting_wod_crystalshard',
  type: 'DOMINATION',
  group: 'domination-blood',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationJas',
  rank: 4,
}, {
  shortName: 'portentous_shard_of_jas',
  name: 'Portentous Jas',
  id: 187313,
  quality: 4,
  icon: 'inv_enchanting_wod_crystalshard',
  type: 'DOMINATION',
  group: 'domination-blood',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationJas',
  rank: 5,
}, {
  shortName: 'shard_of_rev',
  name: 'Shard of Rev',
  id: 187061,
  quality: 4,
  icon: 'inv_enchanting_wod_crystalshard',
  type: 'DOMINATION',
  group: 'domination-blood',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationRev',
  rank: 1,
}, {
  shortName: 'ominous_shard_of_rev',
  name: 'Ominous Rev',
  id: 187286,
  quality: 4,
  icon: 'inv_enchanting_wod_crystalshard',
  type: 'DOMINATION',
  group: 'domination-blood',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationRev',
  rank: 2,
}, {
  shortName: 'desolate_shard_of_rev',
  name: 'Desolate Rev',
  id: 187295,
  quality: 4,
  icon: 'inv_enchanting_wod_crystalshard',
  type: 'DOMINATION',
  group: 'domination-blood',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationRev',
  rank: 3,
}, {
  shortName: 'foreboding_shard_of_rev',
  name: 'Foreboding Rev',
  id: 187304,
  quality: 4,
  icon: 'inv_enchanting_wod_crystalshard',
  type: 'DOMINATION',
  group: 'domination-blood',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationRev',
  rank: 4,
}, {
  shortName: 'portentous_shard_of_rev',
  name: 'Portentous Rev',
  id: 187314,
  quality: 4,
  icon: 'inv_enchanting_wod_crystalshard',
  type: 'DOMINATION',
  group: 'domination-blood',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationRev',
  rank: 5,
}, {
  shortName: 'shard_of_cor',
  name: 'Shard of Cor',
  id: 187063,
  quality: 4,
  icon: 'artifactability_frostmage_blackicicles',
  type: 'DOMINATION',
  group: 'domination-frost',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationCor',
  dps: true,
  rank: 1,
}, {
  shortName: 'ominous_shard_of_cor',
  name: 'Ominous Cor',
  id: 187287,
  quality: 4,
  icon: 'artifactability_frostmage_blackicicles',
  type: 'DOMINATION',
  group: 'domination-frost',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationCor',
  dps: true,
  rank: 2,
}, {
  shortName: 'desolate_shard_of_cor',
  name: 'Desolate Cor',
  id: 187296,
  quality: 4,
  icon: 'artifactability_frostmage_blackicicles',
  type: 'DOMINATION',
  group: 'domination-frost',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationCor',
  dps: true,
  rank: 3,
}, {
  shortName: 'foreboding_shard_of_cor',
  name: 'Foreboding Cor',
  id: 187305,
  quality: 4,
  icon: 'artifactability_frostmage_blackicicles',
  type: 'DOMINATION',
  group: 'domination-frost',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationCor',
  dps: true,
  rank: 4,
}, {
  shortName: 'portentous_shard_of_cor',
  name: 'Portentous Cor',
  id: 187315,
  quality: 4,
  icon: 'artifactability_frostmage_blackicicles',
  type: 'DOMINATION',
  group: 'domination-frost',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationCor',
  dps: true,
  rank: 5,
}, {
  shortName: 'shard_of_tel',
  name: 'Shard of Tel',
  id: 187071,
  quality: 4,
  icon: 'artifactability_frostmage_blackicicles',
  type: 'DOMINATION',
  group: 'domination-frost',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationTel',
  rank: 1,
}, {
  shortName: 'ominous_shard_of_tel',
  name: 'Ominous Tel',
  id: 187289,
  quality: 4,
  icon: 'artifactability_frostmage_blackicicles',
  type: 'DOMINATION',
  group: 'domination-frost',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationTel',
  rank: 2,
}, {
  shortName: 'desolate_shard_of_tel',
  name: 'Desolate Tel',
  id: 187298,
  quality: 4,
  icon: 'artifactability_frostmage_blackicicles',
  type: 'DOMINATION',
  group: 'domination-frost',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationTel',
  rank: 3,
}, {
  shortName: 'foreboding_shard_of_tel',
  name: 'Foreboding Tel',
  id: 187307,
  quality: 4,
  icon: 'artifactability_frostmage_blackicicles',
  type: 'DOMINATION',
  group: 'domination-frost',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationTel',
  rank: 4,
}, {
  shortName: 'portentous_shard_of_tel',
  name: 'Portentous Tel',
  id: 187317,
  quality: 4,
  icon: 'artifactability_frostmage_blackicicles',
  type: 'DOMINATION',
  group: 'domination-frost',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationTel',
  rank: 5,
}, {
  shortName: 'shard_of_kyr',
  name: 'Shard of Kyr',
  id: 187065,
  quality: 4,
  icon: 'artifactability_frostmage_blackicicles',
  type: 'DOMINATION',
  group: 'domination-frost',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationKyr',
  rank: 1,
}, {
  shortName: 'ominous_shard_of_kyr',
  name: 'Ominous Kyr',
  id: 187288,
  quality: 4,
  icon: 'artifactability_frostmage_blackicicles',
  type: 'DOMINATION',
  group: 'domination-frost',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationKyr',
  rank: 2,
}, {
  shortName: 'desolate_shard_of_kyr',
  name: 'Desolate Kyr',
  id: 187297,
  quality: 4,
  icon: 'artifactability_frostmage_blackicicles',
  type: 'DOMINATION',
  group: 'domination-frost',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationKyr',
  rank: 3,
}, {
  shortName: 'foreboding_shard_of_kyr',
  name: 'Foreboding Kyr',
  id: 187306,
  quality: 4,
  icon: 'artifactability_frostmage_blackicicles',
  type: 'DOMINATION',
  group: 'domination-frost',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationKyr',
  rank: 4,
}, {
  shortName: 'portentous_shard_of_kyr',
  name: 'Portentous Kyr',
  id: 187316,
  quality: 4,
  icon: 'artifactability_frostmage_blackicicles',
  type: 'DOMINATION',
  group: 'domination-frost',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationKyr',
  rank: 5,
}, {
  shortName: 'shard_of_dyz',
  name: 'Shard of Dyz',
  id: 187073,
  quality: 4,
  icon: 'spell_priest_mindspike',
  type: 'DOMINATION',
  group: 'domination-unholy',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationDyz',
  dps: true,
  rank: 1,
}, {
  shortName: 'ominous_shard_of_dyz',
  name: 'Ominous Dyz',
  id: 187290,
  quality: 4,
  icon: 'spell_priest_mindspike',
  type: 'DOMINATION',
  group: 'domination-unholy',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationDyz',
  dps: true,
  rank: 2,
}, {
  shortName: 'desolate_shard_of_dyz',
  name: 'Desolate Dyz',
  id: 187299,
  quality: 4,
  icon: 'spell_priest_mindspike',
  type: 'DOMINATION',
  group: 'domination-unholy',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationDyz',
  dps: true,
  rank: 3,
}, {
  shortName: 'foreboding_shard_of_dyz',
  name: 'Foreboding Dyz',
  id: 187308,
  quality: 4,
  icon: 'spell_priest_mindspike',
  type: 'DOMINATION',
  group: 'domination-unholy',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationDyz',
  dps: true,
  rank: 4,
}, {
  shortName: 'portentous_shard_of_dyz',
  name: 'Portentous Dyz',
  id: 187318,
  quality: 4,
  icon: 'spell_priest_mindspike',
  type: 'DOMINATION',
  group: 'domination-unholy',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationDyz',
  dps: true,
  rank: 5,
}, {
  shortName: 'shard_of_zed',
  name: 'Shard of Zed',
  id: 187079,
  quality: 4,
  icon: 'spell_priest_mindspike',
  type: 'DOMINATION',
  group: 'domination-unholy',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationZed',
  rank: 1,
}, {
  shortName: 'ominous_shard_of_zed',
  name: 'Ominous Zed',
  id: 187292,
  quality: 4,
  icon: 'spell_priest_mindspike',
  type: 'DOMINATION',
  group: 'domination-unholy',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationZed',
  rank: 2,
}, {
  shortName: 'desolate_shard_of_zed',
  name: 'Desolate Zed',
  id: 187301,
  quality: 4,
  icon: 'spell_priest_mindspike',
  type: 'DOMINATION',
  group: 'domination-unholy',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationZed',
  rank: 3,
}, {
  shortName: 'foreboding_shard_of_zed',
  name: 'Foreboding Zed',
  id: 187310,
  quality: 4,
  icon: 'spell_priest_mindspike',
  type: 'DOMINATION',
  group: 'domination-unholy',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationZed',
  rank: 4,
}, {
  shortName: 'portentous_shard_of_zed',
  name: 'Portentous Zed',
  id: 187320,
  quality: 4,
  icon: 'spell_priest_mindspike',
  type: 'DOMINATION',
  group: 'domination-unholy',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationZed',
  rank: 5,
}, {
  shortName: 'shard_of_oth',
  name: 'Shard of Oth',
  id: 187076,
  quality: 4,
  icon: 'spell_priest_mindspike',
  type: 'DOMINATION',
  group: 'domination-unholy',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationOth',
  rank: 1,
}, {
  shortName: 'ominous_shard_of_oth',
  name: 'Ominous Oth',
  id: 187291,
  quality: 4,
  icon: 'spell_priest_mindspike',
  type: 'DOMINATION',
  group: 'domination-unholy',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationOth',
  rank: 2,
}, {
  shortName: 'desolate_shard_of_oth',
  name: 'Desolate Oth',
  id: 187300,
  quality: 4,
  icon: 'spell_priest_mindspike',
  type: 'DOMINATION',
  group: 'domination-unholy',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationOth',
  rank: 3,
}, {
  shortName: 'foreboding_shard_of_oth',
  name: 'Foreboding Oth',
  id: 187309,
  quality: 4,
  icon: 'spell_priest_mindspike',
  type: 'DOMINATION',
  group: 'domination-unholy',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationOth',
  rank: 4,
}, {
  shortName: 'portentous_shard_of_oth',
  name: 'Portentous Oth',
  id: 187319,
  quality: 4,
  icon: 'spell_priest_mindspike',
  type: 'DOMINATION',
  group: 'domination-unholy',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  unique: 'dominationOth',
  rank: 5,
}, {

  //////////////////////////////////////////////////
  // Shadowlands Gems
  //////////////////////////////////////////////////

  shortName: '16crit',
  name: '16 Crit',
  id: 173127,
  quality: 3,
  //unique: false,
  icon: 'inv_jewelcrafting_90_rarecut_orange',
  group: 'sl-rare',
  type: 'PRISMATIC',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  stat: {
    type: 'crit',
    amount: 16,
  },
}, {
  shortName: '16haste',
  name: '16 Haste',
  id: 173128,
  quality: 3,
  //unique: false,
  icon: 'inv_jewelcrafting_90_rarecut_yellow',
  group: 'sl-rare',
  type: 'PRISMATIC',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  stat: {
    type: 'haste',
    amount: 16,
  },
}, {
  shortName: '16mastery',
  name: '16 Mastery',
  id: 173130,
  quality: 3,
  //unique: false,
  icon: 'inv_jewelcrafting_90_rarecut_purple',
  group: 'sl-rare',
  type: 'PRISMATIC',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  stat: {
    type: 'mastery',
    amount: 16,
  },
}, {
  shortName: '16vers',
  name: '16 Vers',
  id: 173129,
  quality: 3,
  //unique: false,
  icon: 'inv_jewelcrafting_90_rarecut_blue',
  group: 'sl-rare',
  type: 'PRISMATIC',
  preferred: {
    roles: ['melee', 'caster', 'ranged', 'tank', 'healer'],
  },
  stat: {
    type: 'vers',
    amount: 16,
  },
}, {

  shortName: '12crit',
  name: '12 Crit',
  id: 173121,
  quality: 2,
  //unique: false,
  icon: 'inv_jewelcrafting_90_cutuncommon_orange',
  group: 'sl-uncommon',
  type: 'PRISMATIC',
  stat: {
    type: 'crit',
    amount: 12,
  },
}, {
  shortName: '12haste',
  name: '12 Haste',
  id: 173122,
  quality: 2,
  //unique: false,
  icon: 'inv_jewelcrafting_90_cutuncommon_yellow',
  group: 'sl-uncommon',
  type: 'PRISMATIC',
  stat: {
    type: 'haste',
    amount: 12,
  },
}, {
  shortName: '12mastery',
  name: '12 Mastery',
  id: 173124,
  quality: 2,
  //unique: false,
  icon: 'inv_jewelcrafting_90_cutuncommon_purple',
  group: 'sl-uncommon',
  type: 'PRISMATIC',
  stat: {
    type: 'mastery',
    amount: 12,
  },
}, {
  shortName: '12vers',
  name: '12 Vers',
  id: 173123,
  quality: 2,
  //unique: false,
  icon: 'inv_jewelcrafting_90_cutuncommon_blue',
  group: 'sl-uncommon',
  type: 'PRISMATIC',
  stat: {
    type: 'vers',
    amount: 12,
  },
}, {

  // JC Gems

  shortName: '7agi',
  name: '7 Agility',
  id: 168637,
  quality: 4,
  unique: 468,
  icon: 'inv_misc_metagem_b',
  group: 'main_stat',
  type: 'PRISMATIC',
  stat: {
    type: 'agi',
    amount: 7,
  },
}, {
  shortName: '7int',
  name: '7 Intellect',
  id: 168638,
  quality: 4,
  unique: 468,
  icon: 'inv_misc_metagem_b',
  group: 'main_stat',
  type: 'PRISMATIC',
  stat: {
    type: 'int',
    amount: 7,
  },
}, {
  shortName: '7str',
  name: '7 Strength',
  id: 168636,
  quality: 4,
  unique: 468,
  icon: 'inv_misc_metagem_b',
  group: 'main_stat',
  type: 'PRISMATIC',
  stat: {
    type: 'str',
    amount: 7,
  },

}, {

  shortName: '6agi',
  name: '6 Agility',
  id: 153708,
  quality: 4,
  unique: 463,
  icon: 'inv_jewelcrafting_80_specialgemcut01',
  group: 'main_stat',
  type: 'PRISMATIC',
  stat: {
    type: 'agi',
    amount: 6,
  },
}, {
  shortName: '6int',
  name: '6 Intellect',
  id: 153709,
  quality: 4,
  unique: 463,
  icon: 'inv_jewelcrafting_80_specialgemcut01',
  group: 'main_stat',
  type: 'PRISMATIC',
  stat: {
    type: 'int',
    amount: 6,
  },
}, {
  shortName: '6str',
  name: '6 Strength',
  id: 153707,
  quality: 4,
  unique: 463,
  icon: 'inv_jewelcrafting_80_specialgemcut01',
  group: 'main_stat',
  type: 'PRISMATIC',
  stat: {
    type: 'str',
    amount: 6,
  },

}, {

  shortName: '7crit',
  name: '7 Crit',
  id: 168639,
  quality: 4,
  // unique: false,
  icon: 'inv_misc_gem_x4_uncommon_perfectcut_orange',
  group: 'epic',
  type: 'PRISMATIC',
}, {
  shortName: '7haste',
  name: '7 Haste',
  id: 168641,
  quality: 4,
  // unique: false,
  icon: 'inv_misc_gem_x4_uncommon_perfectcut_yellow',
  group: 'epic',
  type: 'PRISMATIC',
}, {
  shortName: '7mastery',
  name: '7 Mastery',
  id: 168640,
  quality: 4,
  // unique: false,
  icon: 'inv_misc_gem_x4_uncommon_perfectcut_purple',
  group: 'epic',
  type: 'PRISMATIC',
}, {
  shortName: '7vers',
  name: '7 Vers',
  id: 168642,
  quality: 4,
  // unique: false,
  icon: 'inv_misc_gem_x4_uncommon_perfectcut_blue',
  group: 'epic',
  type: 'PRISMATIC',
}, {
  shortName: '6crit',
  name: '6 Crit',
  id: 154126,
  quality: 3,
  // unique: false,
  icon: 'inv_jewelcrafting_80_cutgem02_orange',
  group: 'rare',
  type: 'PRISMATIC',
}, {
  shortName: '6haste',
  name: '6 Haste',
  id: 154127,
  quality: 3,
  // unique: false,
  icon: 'inv_jewelcrafting_80_cutgem02_yellow',
  group: 'rare',
  type: 'PRISMATIC',
}, {
  shortName: '6mastery',
  name: '6 Mastery',
  id: 154129,
  quality: 3,
  // unique: false,
  icon: 'inv_jewelcrafting_80_cutgem02_purple',
  group: 'rare',
  type: 'PRISMATIC',
}, {
  shortName: '6vers',
  name: '6 Vers',
  id: 154128,
  quality: 3,
  // unique: false,
  icon: 'inv_jewelcrafting_80_cutgem02_blue',
  group: 'rare',
  type: 'PRISMATIC',
}, {
  shortName: '4crit',
  name: '4 Crit',
  id: 153710,
  quality: 2,
  // unique: false,
  icon: 'inv_jewelcrafting_80_cutgem01_orange',
  group: 'uncommon',
  type: 'PRISMATIC',
}, {
  shortName: '4haste',
  name: '4 Haste',
  id: 153711,
  quality: 2,
  // unique: false,
  icon: 'inv_jewelcrafting_80_cutgem01_yellow',
  group: 'uncommon',
  type: 'PRISMATIC',
}, {
  shortName: '4mastery',
  name: '4 Mastery',
  id: 153713,
  quality: 2,
  // unique: false,
  icon: 'inv_jewelcrafting_80_cutgem01_purple',
  group: 'uncommon',
  type: 'PRISMATIC',
}, {
  shortName: '4vers',
  name: '4 Vers',
  id: 153712,
  quality: 2,
  // unique: false,
  icon: 'inv_jewelcrafting_80_cutgem01_blue',
  group: 'uncommon',
  type: 'PRISMATIC',
}];

export const enhancements = _.union(
  enchants,
  _.map(gemInfo, gem => ({
    ...gem,
    slot: 'socket',
  })),
);

export const findEnhancement = enhancementId => _.find(enhancements, { id: enhancementId });

export const uniqueGems = _.fromPairs(_.map(_.filter(gemInfo, g => g.unique), g => [g.shortName, g.name]));
export const normalGems = _.fromPairs(_.map(_.filter(gemInfo, g => !g.unique), g => [g.shortName, g.name]));

export const gems = _.fromPairs(_.map(_.filter(gemInfo, 'shortName'), g => [g.shortName, g.name]));

export const getGemDetails = gemId => _.find(gemInfo, { id: parseInt(gemId, 10) });
export const isGemUnique = gemId => _.get(getGemDetails(gemId), 'unique', false);

export const enchantableSlots = _.union(
  // need to look up all the inventor types that this slot _could_ use
  // This is the ol' chestnut of multiple inv types used for chest slot
  _.flatten(_.map(enchants, e => {
    return _.map(_.keys(_.pickBy(slotMap, v => v === slotMap[e.slot])), k => parseInt(k, 10));
  })),
  _.map(
    _.keys(_.pickBy(slotMap, s => _.includes(['main_hand', 'off_hand'], s))),
    s => parseInt(s, 10),
  ),
);

export const enhancementsForSlot = slot => _.filter(enhancements, { slot });
export const enhancementsForSlotName = slotName => enhancementsForSlot(invertedSlotMap[slotName]);

export const getEnhancementsForSlotNameAndSpec = (slotName, specInfo) => {
  if (!slotName || !specInfo) {
    return [];
  }
  const classInfo = specInfo.class;
  let slotEnhancements;
  if (slotName === 'socket') {
    slotEnhancements = enhancementsForSlot('socket');
  } else {
    slotEnhancements = enhancementsForSlotName(slotName === 'off_hand' ? 'main_hand' : slotName);
  }
  return _.sortBy(
    _.filter(slotEnhancements, enchant => {
      // check for class requirement (e.g., DK weapon enchants)
      const requiredClasses = _.get(enchant, 'requirement.classes', null);
      if (requiredClasses) {
        // reject if no class info or if character is not the required class
        if (!classInfo || !_.includes(requiredClasses, classInfo.id)) {
          return false;
        }
      }

      return true;
    }),
    enhancement => _.get(enhancement, 'requirement.classes', Infinity),
  );
};

export const isPreferredEnhancement = (enhancement, specInfo) => {
  const preferredRole = _.includes(_.get(enhancement, 'preferred.roles'), specInfo.role);
  const preferredSpec = _.includes(_.get(enhancement, 'preferred.specs'), specInfo.id);

  const preferred = preferredRole || preferredSpec;

  // DKs only want DK weapon enchants
  if (enhancement.slot === MAIN_HAND_SLOT && _.includes([250, 251, 252], specInfo.id)) {
    return preferred && enhancement.group === 'death_knight';
  }

  return preferred;
};
export const getPreferredEnhancements = (slot, specInfo) => {
  return _.filter(
    getEnhancementsForSlotNameAndSpec(slot, specInfo),
    enhancement => isPreferredEnhancement(enhancement, specInfo),
  );
};

// aliases
export const getEnchant = id => _.find(enchants, { id });
export const enchantsForSlot = enhancementsForSlot;
export const enchantsForSlotName = enhancementsForSlotName;
export const getEnchantsForSlotNameAndSpec = getEnhancementsForSlotNameAndSpec;


export const enchantsForItem = item => enhancementsForSlot(
  // get the internal "canonical" slot
  invertedSlotMap[slotMap[item.inventoryType]],
);

export const isEnchantable = item => {
  return item && _.includes(enchantableSlots, item.inventoryType);
};

export const isAzeriteArmor = item => item && !!item.azeritePowerSetId;

export const canRollSecondary = item => (
  item
  && item.chanceBonusList
  // compare secondary bonus IDs against chance bonus list, return true if any
  // bonus changes are presetn
  && _.intersection(_.map(secondaryBonuses, sb => sb.bonusId), item.chanceBonusList).length > 0
);

export const isTrinket = item => item && slotMap[item.inventoryType] === 'trinket';

export const isRingOrTrinket = item => (
  item && _.includes(['finger', 'trinket'], slotMap[item.inventoryType])
);

export const defaultSlot = (item) => {
  const slotName = slotMap[item.inventoryType];
  if (isRingOrTrinket(item)) {
    return `${slotName}1`;
  }
  return slotName;
};

export const SOCKET_TYPES = {
  PRISMATIC: 'PRISMATIC',
  DOMINATION: 'DOMINATION',
};

const getSocketTypeInfo = (socketInfo, type) => {
  return socketInfo ? socketInfo[type] : null;
};

export const numStaticSockets = (item, type) => {
  if (!type) {
    throw new Error('numStaticSockets called without a socket type');
  }
  const socketInfo = getSocketTypeInfo(item?.socketInfo, type);
  if (socketInfo) {
    return socketInfo.staticSlots;
  }
  if (item?.socketInfo?.sockets) {
    return _.size(_.filter(item.socketInfo.sockets, { type }));
  }
  return 0;
};

const ROLLABLE_SOCKET_TYPES = [
  'PRISMATIC',
];

export const numDynamicSockets = (item, type) => {
  if (!type) {
    throw new Error('numDynamicSockets called without a socket type');
  }
  if (_.includes(ROLLABLE_SOCKET_TYPES, type)) {
    if (item && item.bonus_id) {
      const bonusIds = _.map(_.split(item.bonus_id, '/'), b => parseInt(b, 10));
      if (_.intersection(socketBonuses, bonusIds).length > 0) {
        return 1;
      }
    }
  }
  return 0;
};

const ITEMS_NO_DYNAMIC_SOCKET = [
  // pocket-sized computation device
  167555,
];

export const MAX_SOCKETS = 6;

export const SOCKETABLE_SLOTS = [
  'head',
  'finger',
  'neck',
  'wrist',
  'waist',
];

export const canRollSocket = (item, type) => {
  if (!type) {
    throw new Error('canRollSocket called without a socket type');
  }

  if (!_.includes(ROLLABLE_SOCKET_TYPES, type)) {
    return false;
  }

  if (!item) {
    return false;
  }

  const socketInfo = getSocketTypeInfo(item.socketInfo, type);

  // already has a socket
  if (socketInfo?.staticSlots > 0 || socketInfo?.dynamicSlots > 0) {
    return false;
  }

  // domination gear can't roll prismatic sockets
  if (type === SOCKET_TYPES.PRISMATIC) {
    let hasDomSocket = false;
    // full socket info data structure
    if (item.socketInfo && item.socketInfo[SOCKET_TYPES.DOMINATION]?.total > 0) {
      hasDomSocket = true;
    }
    // client-side data structure, shows in Droptimizer, probably should figure out how to get rid of this structure
    if (item.socketInfo?.sockets) {
      hasDomSocket = _.find(item.socketInfo.sockets, { type: SOCKET_TYPES.DOMINATION });
    }
    if (hasDomSocket) {
      return false;
    }
  }

  // artifact
  if (item.quality === 6) {
    return false;
  }

  // legendary
  if (item.quality === 5) {
    // Old legendaries cannot be socketed, but shadowlands can be
    if (item.expansion < 8) {
      return false;
    }
  }

  if (isAzeriteArmor(item)) {
    return false;
  }

  // deny list of items that cannot be socketed even if they fulfill other constraints
  if (_.includes(ITEMS_NO_DYNAMIC_SOCKET, item.id)) {
    return false;
  }

  // shadowlands sockets are limited to head, neck, rings, bracers, and belt
  // Just apply this to everything. There was an expansion check but that was doing weird stuff
  // for timewalking items
  if (!_.includes(SOCKETABLE_SLOTS, slotMap[item.inventoryType])) {
    return false;
  }

  return true;
};

export const numSockets = (item, type) => {
  if (!type) {
    throw new Error('canRollSocket called without a socket type');
  }

  const staticSockets = numStaticSockets(item, type);
  const dynamicSockets = canRollSocket(item, type) ? 1 : 0;

  if (staticSockets) {
    return staticSockets;
  }
  return dynamicSockets;
};

export const possibleSockets = item => {
  if (!item) {
    return [];
  }

  const sockets = [];

  if (canRollSocket(item, SOCKET_TYPES.PRISMATIC)) {
    sockets.push(SOCKET_TYPES.PRISMATIC);
  }

  if (item.socketInfo) {
    _.each(_.keys(item.socketInfo), type => sockets.push(type));
  }

  return _.uniq(sockets);
};

export const itemSocketInfo = item => {
  const types = {};
  _.each(SOCKET_TYPES, type => {
    const itemSocketTypeInfo = item.socketInfo ? item.socketInfo[type] : null;
    // keep any static slots found from web item lookups
    // frontend item lookups don't have access to the full game data
    const staticSlots = itemSocketTypeInfo?.staticSlots || numStaticSockets(item, type);
    const dynamicSlots = numDynamicSockets(item, type);
    const gemDetails = _.filter(
      _.map(_.filter(_.split(item.gem_id, '/')), g => getGemDetails(parseInt(g, 10))),
      gem => gem && gem.type === type,
    );
    const gemIds = _.map(gemDetails, 'id');
    const hasUniqueGem = _.size(_.filter(gemIds, isGemUnique)) > 0;
    if (staticSlots || dynamicSlots || gemIds.length > 0) {
      types[type] = {
        type,
        staticSlots,
        dynamicSlots,
        filled: gemIds.length,
        total: staticSlots + dynamicSlots,
        gems: gemDetails,
        gemIds,
        hasUnique: hasUniqueGem,
      };
    }
  });
  return types;
};

export const isLegionLegendary = item => {
  if (item && item.itemLimit && item.itemLimit.category) {
    const result = item && item.quality === 5 && item.itemLimit.category === LEGION_LEGENDARY_CATEGORY;
    return result;
  }
  return false;
};

export const isEternalPalaceItem = item => {
  return ETERNAL_PALACE_ITEM_IDS.has(item.id);
};

export const canRollWrathionSocket = (item) => {
  if (!item) {
    return false;
  }
  if (!canRollSocket(item, SOCKET_TYPES.PRISMATIC)) {
    return false;
  }

  // ignore items that already have a socket
  const socketInfo = item.socketInfo || itemSocketInfo(item);
  if (socketInfo.total > 0) {
    return false;
  }

  if (isEternalPalaceItem(item)) {
    return false;
  }

  return true;
};

const CRAFT_STAT_1 = _.find(STATS, { enum: 'STAT_CRAFT_MOD_1' });
const CRAFT_STAT_2 = _.find(STATS, { enum: 'STAT_CRAFT_MOD_1' });
export const hasCraftedStats = item => {
  if (!item) {
    return false;
  }

  const hasCraftStat1 = _.some(item.stats, { id: CRAFT_STAT_1.dataId });
  const hasCraftStat2 = _.some(item.stats, { id: CRAFT_STAT_2.dataId });
  return hasCraftStat1 && hasCraftStat2;
};

export const CRAFTED_STATS = [{
  id: 32,
  name: 'crit',
}, {
  id: 36,
  name: 'haste',
}, {
  id: 49,
  name: 'mastery',
}, {
  id: 40,
  name: 'vers',
}];

export const CRAFTED_STAT_COMBOS = _.sortBy(
  _.union(
    _.map(
      CRAFTED_STATS,
      stat => ({
        id: stat.id,
        name: _.startCase(stat.name),
        value: stat.name,
      }),
    ),
    _.map(
      Combinatorics.combination(CRAFTED_STATS, 2).toArray(),
      combo => {
        return {
          id: _.join(_.map(combo, 'id'), '/'),
          name: _.join(_.map(combo, c => _.startCase(c.name)), '/'),
          value: _.join(_.map(combo, 'name'), ','),
        };
      },
    ),
  ),
  'name',
);
